import { Role } from '@/utility/role';
import { atom } from 'jotai';

/**
 * @constant 初期値
 */
const initValue = '1';

/**
 * @constant userRoleBaseAtom
 * 下記atomなどが参照する大本のatom
 * ほかのatomを嚙ませることで、getter/setterの動きをある程度定義する
 * この `atom` は export してはならない
 */
const userRoleBaseAtom = atom(initValue);
userRoleBaseAtom.debugLabel = 'userRoleBaseAtom';

/**
 * @constant UserRoleAtom
 * 利用者に割り当てられた管理ロールを保持する
 * nullが当たっている場合は初期値を返却する
 */
export const userRoleAtom = atom(
  (get) => get(userRoleBaseAtom),
  (_get, set, updateValue: number | string) =>
    set(userRoleBaseAtom, typeof updateValue === 'number' ? updateValue.toString() : updateValue)
);
userRoleAtom.debugLabel = 'userRoleAtom';

/**
 * @constant isCommonUserRoleAtom
 * 利用者に割り当てられたロールが非会員ユーザのものかどうかを返すatom
 * true: 非会員ユーザ
 */
export const isCommonUserRoleAtom = atom((get) => parseInt(get(userRoleBaseAtom), 10) < Role.MEMBER);
isCommonUserRoleAtom.debugLabel = 'isCommonUserRoleAtom';

/**
 * @constant isGeneralRoleAtom
 * 利用者に割り当てられたロールが一般ユーザ（非ログイン/ログイン）のものかどうかを返すatom
 * true: 非会員ユーザ
 */
export const isGeneralRoleAtom = atom((get) => parseInt(get(userRoleBaseAtom), 10) <= Role.MEMBER);
isGeneralRoleAtom.debugLabel = 'isGeneralRoleAtom';

/**
 * @constant isAdminRoleAtom
 * 利用者に割り当てられたロールが不動産会社/コンサルタント/管理者のものかどうかを返すatom
 * true: 管理ユーザ
 */
export const isAdminRoleAtom = atom((get) =>
  [Role.REAL_ESTATE, Role.CONSULTANT, Role.ADMIN].includes(parseInt(get(userRoleBaseAtom), 10))
);
isAdminRoleAtom.debugLabel = 'isAdminRoleAtom';

/**
 * @constant isAdminConsultantRoleAtom
 * 利用者に割り当てられたロールが管理ユーザ/コンサルタントかどうかを返すatom
 * true: 管理ユーザ/コンサルタント
 */
export const isAdminConsultantRoleAtom = atom((get) =>
  [Role.CONSULTANT, Role.ADMIN].includes(parseInt(get(userRoleBaseAtom), 10))
);
isAdminConsultantRoleAtom.debugLabel = 'isAdminConsultantRoleAtom';

/**
 * @constant isAdminUserRoleAtom
 * 利用者に割り当てられたロールが管理ユーザかどうかを返すatom
 * true: 管理ユーザ/コンサルタント
 */
export const isAdminUserRoleAtom = atom((get) => parseInt(get(userRoleBaseAtom), 10) === Role.ADMIN);
isAdminUserRoleAtom.debugLabel = 'isAdminUserRoleAtom';

/**
 * @constant isConsultantUserRoleAtom
 * 利用者に割り当てられたロールがコンサルタントユーザかどうかを返すatom
 * true: 管理ユーザ/コンサルタント
 */
export const isConsultantUserRoleAtom = atom((get) => parseInt(get(userRoleBaseAtom), 10) === Role.CONSULTANT);
isConsultantUserRoleAtom.debugLabel = 'isConsultantUserRoleAtom';

/**
 * @constant isEstateUserRoleAtom
 * 利用者に割り当てられたロールが不動産会社ユーザかどうかを返すatom
 * true: 管理ユーザ/コンサルタント
 */
export const isEstateUserRoleAtom = atom((get) => parseInt(get(userRoleBaseAtom), 10) === Role.REAL_ESTATE);
isEstateUserRoleAtom.debugLabel = 'isEstateUserRoleAtom';

/**
 * @constant isDesignUserRoleAtom
 * 利用者に割り当てられたロールが内装会社ユーザかどうかを返すatom
 * true: 内装会社ユーザ
 */
export const isDesignUserRoleAtom = atom((get) => parseInt(get(userRoleBaseAtom), 10) === Role.DESIGN);
isDesignUserRoleAtom.debugLabel = 'isDesignUserRoleAtom';

/**
 * @constant isInsideSalesUserRoleAtom
 * 利用者に割り当てられたロールがインサイドセールスユーザかどうかを返すatom
 * true: インサイドセールスユーザ
 */
export const isInsideSalesUserRoleAtom = atom((get) => parseInt(get(userRoleBaseAtom), 10) === Role.INSIDE_SALES);
isInsideSalesUserRoleAtom.debugLabel = 'isInsideSalesUserRoleAtom';

/**
 * デバッグ用
 * 現在のRoleIDを返す
 */
export const getAtomVal = atom((get) => get(userRoleBaseAtom));

export default userRoleAtom;
